import ClassificationSection from "../api/model/classification-section";
import Product from "../api/model/comparable-product";
import { createStore } from "@stencil/store";
import { ProductComparisonResponse } from "../api/responses/product-comparison-response";
import config from "environment/environment-config";
import { Entry } from "api/model/core";

interface ProductComparisonStore {
  maxProductsQuantity: number;
  currentProductsQuantity: number;
  classificationSections: ClassificationSection[];
  products: Product[];
  errors: string[];
  loading: boolean;
  initialized: boolean;
  is: "INIT" | "LOADING" | "LOADED" | "ERROR";
}

const { state, set, get } = createStore({
  maxProductsQuantity: 5,
  currentProductsQuantity: 0,
  classificationSections: [],
  products: [],
  errors: [],
  loading: false,
  initialized: false,
} as ProductComparisonStore);

const productComparisonUrl = config.apiUrls.productComparisonApi;

/**
 * Returns default headers for JSON request to API.
 */
function getHeaders() {
  return {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  };
}

const handleErrors = () => {
  set("is", "ERROR");
};

/**
 * Saves data received from API in store. Products data transforms to Entry List (transformProductsToEntryList).
 *
 * @see transformProductsToEntryList
 *
 * @param classificationSections - classification
 * @param currentProductsQuantity - count of products on list
 * @param maxProductsQuantity - count of maximum product quantity
 * @param products - list of products
 */
function handleResponse({
  classificationSections,
  currentProductsQuantity,
  maxProductsQuantity,
  products,
}: ProductComparisonResponse): void {
  set("maxProductsQuantity", maxProductsQuantity);
  set("currentProductsQuantity", currentProductsQuantity);
  if (classificationSections?.length > 0) {
    set("classificationSections", classificationSections);
  }
  set("products", products);
}

/**
 * Fetches data from the server related to product comparison (configuration and list of products added to comparison).
 * Updates state with response data.
 */
export async function getComparisonData(
  classificationConfiguration?: string
): Promise<Entry[] | void> {
  if (get("loading")) {
    return;
  }

  if (get("initialized")) {
    return transformProductsToEntryList();
  }

  const url =
    classificationConfiguration == null
      ? productComparisonUrl
      : `${productComparisonUrl}?${classificationConfiguration}`;

  set("loading", true);

  return fetch(url, {
    method: "GET",
    ...getHeaders(),
  })
    .then(async (response) => {
      if (!response.ok) {
        handleErrors();
        return;
      }
      const comparisonResponse: ProductComparisonResponse =
        await response.json();
      handleResponse(comparisonResponse);
      set("initialized", true);

      return transformProductsToEntryList();
    })
    .catch(handleErrors)
    .finally(() => {
      set("loading", false);
    });
}

/**
 * Transforming list of product to Entry list.
 *
 * @see Product
 * @see Entry
 *
 * @param productList - product list
 */
function transformProductsToEntryList(): Entry[] {
  const products: Product[] = get("products");
  return (
    products.map(({ name, ean, code, sku, url, purchasable }) => ({
      product: {
        name,
        codes: {
          ean,
          sku,
          code,
        },
        url,
        purchasable,
      },
    })) ?? []
  );
}

/**
 * Return transformed to Entry, list of Compare products (Product)
 *
 * @see Product
 * @see Entry
 * @see transformProductsToEntryList
 */
export function getCompareEntryList(): Entry[] {
  return transformProductsToEntryList();
}

/**
 * Adds product with provided code to the comparison list. Updates state with response data.
 *
 * @param productCode code of products that should be added to comparison.
 */
export async function addProductToComparison(
  productCode: string
): Promise<void> {
  set("loading", true);

  return fetch(`${productComparisonUrl}/${productCode}`, {
    method: "POST",
    ...getHeaders(),
  })
    .then(async (response) => {
      if (!response.ok) {
        handleErrors();
      }

      const comparisonResponse: ProductComparisonResponse =
        await response.json();
      handleResponse(comparisonResponse);
    })
    .catch(handleErrors)
    .finally(() => set("loading", false));
}

/**
 * Removes product with provided code from the comparison. Update state by removing product from the list if operation
 * was finished with success.
 *
 * @param productCode code of product that should be removed from the comparison.
 */
export const removeProductFromComparison: (
  productCode: string
) => Promise<void> = async (productCode: string) => {
  set("loading", true);
  return fetch(`${productComparisonUrl}/${productCode}`, {
    method: "DELETE",
    ...getHeaders(),
  })
    .then(async (response) => {
      if (!response.ok) {
        handleErrors();
      }
      set("products", [
        ...state.products.filter((product) => productCode !== product.code),
      ]);
      set("currentProductsQuantity", (state.currentProductsQuantity -= 1));
    })
    .catch(handleErrors)
    .finally(() => set("loading", false));
};

/**
 * Removes all products from the comparison. Updates state by clearing product quantity and list.
 */
export const removeAllProductsFromComparison: () => Promise<void> =
  async () => {
    set("loading", true);
    return fetch(productComparisonUrl, {
      method: "DELETE",
      ...getHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          handleErrors();
        }
        set("currentProductsQuantity", 0);
        set("products", []);
      })
      .catch(handleErrors)
      .finally(() => set("loading", false));
  };

export default state;
